import type { EventObject, MetaObject, SetupTypes } from "xstate";
import { assign, setup } from "xstate";

export type T_ThreadActionTarget = {
  cuid: string;
  name: string | null;
};

export type T_ThreadActionContext = {
  thread: T_ThreadActionTarget | null;
  newName: string;
  errorMessage: string | null;
};

export type T_ThreadActionEvent =
  | { type: "OPEN_DELETE"; thread: T_ThreadActionTarget }
  | { type: "OPEN_RENAME"; thread: T_ThreadActionTarget }
  | { type: "RENAME_INPUT"; newName: string }
  | { type: "CLOSE" }
  | { type: "CONFIRM_DELETE" }
  | { type: "DELETE_OK" }
  | { type: "DELETE_ERR"; error: string }
  | { type: "CONFIRM_RENAME" }
  | { type: "RENAME_OK" }
  | { type: "RENAME_ERR"; error: string };

const threadActionSetupTypes: SetupTypes<
  T_ThreadActionContext,
  T_ThreadActionEvent,
  Record<never, string>,
  string,
  unknown,
  unknown,
  EventObject,
  MetaObject
> = {};

const idleContext: T_ThreadActionContext = {
  thread: null,
  newName: "",
  errorMessage: null,
};

/** Canonical rename/delete workflow shared by current shell and legacy chat. */
export const threadActionMachine = setup({
  types: threadActionSetupTypes,
  actions: {
    reset: assign(() => idleContext),
    stageDelete: assign(({ event }) => {
      if (event.type !== "OPEN_DELETE") return {};
      return {
        thread: event.thread,
        newName: "",
        errorMessage: null,
      };
    }),
    stageRename: assign(({ event }) => {
      if (event.type !== "OPEN_RENAME") return {};
      return {
        thread: event.thread,
        newName: event.thread.name ?? "",
        errorMessage: null,
      };
    }),
    updateRenameDraft: assign(({ event }) => {
      if (event.type !== "RENAME_INPUT") return {};
      return { newName: event.newName, errorMessage: null };
    }),
    clearError: assign({ errorMessage: null }),
    applyDeleteError: assign(({ event }) => {
      if (event.type !== "DELETE_ERR") return {};
      return { errorMessage: event.error };
    }),
    applyRenameError: assign(({ event }) => {
      if (event.type !== "RENAME_ERR") return {};
      return { errorMessage: event.error };
    }),
  },
  guards: {
    hasRename: ({ context }) => context.newName.trim().length > 0,
  },
}).createMachine({
  id: "threadAction",
  context: idleContext,
  initial: "idle",
  states: {
    idle: {
      on: {
        OPEN_DELETE: { target: "confirmingDelete", actions: "stageDelete" },
        OPEN_RENAME: { target: "renaming", actions: "stageRename" },
      },
    },
    confirmingDelete: {
      on: {
        CLOSE: { target: "idle", actions: "reset" },
        CONFIRM_DELETE: { target: "deleting", actions: "clearError" },
      },
    },
    deleting: {
      on: {
        DELETE_OK: { target: "idle", actions: "reset" },
        DELETE_ERR: {
          target: "confirmingDelete",
          actions: "applyDeleteError",
        },
      },
    },
    renaming: {
      on: {
        RENAME_INPUT: { actions: "updateRenameDraft" },
        CLOSE: { target: "idle", actions: "reset" },
        CONFIRM_RENAME: {
          target: "savingRename",
          actions: "clearError",
          guard: "hasRename",
        },
      },
    },
    savingRename: {
      on: {
        RENAME_OK: { target: "idle", actions: "reset" },
        RENAME_ERR: { target: "renaming", actions: "applyRenameError" },
      },
    },
  },
});
